import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  useUploadPartSchemas,
  type PartSchemaUploadResponse,
} from "../services/PartSchemaService";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "../utils/errorDetail";
import { useFileDropzone } from "../hooks/useFileDropzone";

interface PartSchemaUploadModalProps {
  open: boolean;
  onClose: () => void;
}

// Upload responses can carry up to MAX_BATCH_PAYLOAD rows of errors (10k).
// Show enough to diagnose without flooding the modal; the rest are summarized.
const MAX_VISIBLE_ROW_ERRORS = 25;

const ACCEPTED_EXTENSIONS = [".xlsx", ".xlsm", ".csv", ".json"];

const PartSchemaUploadModal = ({
  open,
  onClose,
}: PartSchemaUploadModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<PartSchemaUploadResponse | null>(null);

  const uploadSchemas = useUploadPartSchemas();

  const selectFile = useCallback((selected: File) => {
    setFile(selected);
    setResult(null);
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) selectFile(selected);
    },
    [selectFile],
  );

  const { dragActive, dropzoneProps } = useFileDropzone({
    accept: ACCEPTED_EXTENSIONS,
    onFile: selectFile,
    onReject: (rejected) =>
      NucToast({
        message: `Unsupported file "${rejected.name}". Accepted: ${ACCEPTED_EXTENSIONS.join(", ")}`,
        type: "error",
        hideProgressBar: false,
      }),
  });

  const handleSubmit = () => {
    if (!file) return;
    uploadSchemas.mutate(file, {
      onSuccess: (data) => {
        setResult(data);
      },
      onError: (err) => {
        const detail = extractErrorDetail(err);
        console.error("Failed to upload schemas file", err);
        NucToast({
          message: `Failed to upload schemas file: ${detail}`,
          type: "error",
          hideProgressBar: false,
        });
      },
    });
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  // Belt-and-suspenders: if the parent ever toggles `open` to false without
  // routing through handleClose (e.g. a programmatic close), still reset state.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setResult(null);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={uploadSchemas.isLoading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Upload Part Schemas</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Bulk create or update part-type schemas from an Excel, CSV, or JSON
            file. Schemas are matched by <strong>part_type</strong> — existing
            ones are updated (features merged, never deleted) and new ones are
            created.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Excel / CSV:</strong> one row per feature. Required column:{" "}
            <strong>part_type</strong>. Schema columns (description, parent)
            repeat per row; feature columns: canonical_name, display_name,
            data_type (string/integer/float/boolean), unit, required, aliases
            (semicolon-separated). <strong>JSON:</strong> a list of schema
            objects with nested <code>features</code>.
          </Typography>

          <Box
            sx={{
              border: "2px dashed",
              borderColor: dragActive || file ? "primary.main" : "grey.300",
              bgcolor: dragActive ? "action.hover" : "transparent",
              borderRadius: 1,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { borderColor: "primary.main" },
            }}
            component="label"
            {...dropzoneProps}
          >
            <input
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(",")}
              hidden
              onChange={handleFileChange}
            />
            <UploadFileIcon
              sx={{ fontSize: 40, color: file ? "primary.main" : "grey.400" }}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {dragActive
                ? "Drop file to upload"
                : file
                  ? file.name
                  : "Click or drag to upload Excel, CSV, or JSON file"}
            </Typography>
          </Box>

          {result && (
            <Alert severity={result.error_count > 0 ? "warning" : "success"}>
              <AlertTitle>
                {result.created_count} created, {result.updated_count} updated
                {result.error_count > 0 &&
                  ` — ${result.error_count} had errors`}
              </AlertTitle>
              {result.error_count > 0 && result.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <List
                    dense
                    disablePadding
                    sx={{ maxHeight: 200, overflowY: "auto" }}
                  >
                    {result.errors
                      .slice(0, MAX_VISIBLE_ROW_ERRORS)
                      .map(({ part_type, row, error }, i) => (
                        <ListItem
                          key={`${row ?? "?"}-${part_type ?? ""}-${error}-${i}`}
                          disableGutters
                          sx={{ py: 0 }}
                        >
                          <ListItemText
                            primary={`${
                              row !== undefined
                                ? `Row ${row}`
                                : (part_type ?? "")
                            }${part_type && row !== undefined ? ` (${part_type})` : ""}: ${error}`}
                            primaryTypographyProps={{
                              variant: "body2",
                              sx: { fontFamily: "monospace" },
                            }}
                          />
                        </ListItem>
                      ))}
                  </List>
                  {result.errors.length > MAX_VISIBLE_ROW_ERRORS && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      …and {result.errors.length - MAX_VISIBLE_ROW_ERRORS} more.
                    </Typography>
                  )}
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploadSchemas.isLoading}>
          {result ? "Done" : "Cancel"}
        </Button>
        {!result && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!file || uploadSchemas.isLoading}
            startIcon={
              uploadSchemas.isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {uploadSchemas.isLoading ? "Uploading..." : "Upload"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PartSchemaUploadModal;
